import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Clock, 
  Check, 
  X, 
  MessageCircle, 
  Calendar,
  User,
  Mail,
  MapPin,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AdoptionRequest {
  id: string;
  requester_id: string;
  message: string;
  meeting_availability: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  requester_profile: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    bio: string | null;
  };
}

interface AdoptionRequestsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adoptionPostId: string;
  petName: string;
  onRequestUpdate?: () => void;
}

export default function AdoptionRequestsModal({ 
  open, 
  onOpenChange, 
  adoptionPostId, 
  petName,
  onRequestUpdate 
}: AdoptionRequestsModalProps) {
  const [requests, setRequests] = useState<AdoptionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<AdoptionRequest | null>(null);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [actionType, setActionType] = useState<'approved' | 'rejected'>('approved');

  useEffect(() => {
    if (open && adoptionPostId) {
      fetchRequests();
    }
  }, [open, adoptionPostId]);

  const fetchRequests = async () => {
    if (!adoptionPostId) return;

    try {
      setLoading(true);
      
      const { data: requestsData, error } = await supabase
        .from('adoption_requests')
        .select('*')
        .eq('adoption_post_id', adoptionPostId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
        return;
      }

      // Get requester profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url, bio')
        .in('user_id', requestsData.map(r => r.requester_id));

      if (profilesError) throw profilesError;

      // Transform the data
      const transformedRequests = requestsData.map(request => {
        const profile = profilesData?.find(p => p.user_id === request.requester_id);

        return {
          id: request.id,
          requester_id: request.requester_id,
          message: request.message,
          meeting_availability: request.meeting_availability,
          status: request.status as 'pending' | 'approved' | 'rejected',
          created_at: request.created_at,
          updated_at: request.updated_at,
          requester_profile: {
            username: profile?.username || '',
            display_name: profile?.display_name,
            avatar_url: profile?.avatar_url,
            bio: profile?.bio
          }
        };
      });

      setRequests(transformedRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId: string, status: 'approved' | 'rejected', replyMsg: string) => {
    try {
      const { error } = await supabase
        .from('adoption_requests')
        .update({ 
          status,
          reply_message: replyMsg,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Create notification for requester
      const request = requests.find(r => r.id === requestId);
      if (request) {
        await supabase.rpc('create_notification', {
          p_user_id: request.requester_id,
          p_type: `adoption_${status}`,
          p_title: status === 'approved' 
            ? `Richiesta approvata per ${petName}!`
            : `Richiesta rifiutata per ${petName}`,
          p_message: replyMsg,
          p_data: {
            adoption_post_id: adoptionPostId,
            adoption_request_id: requestId
          }
        });
      }

      toast.success(
        status === 'approved' 
          ? 'Richiesta approvata!' 
          : 'Richiesta rifiutata'
      );

      fetchRequests();
      onRequestUpdate?.();
      setReplyModalOpen(false);
      setReplyMessage('');
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleReply = async () => {
    if (!selectedRequest) return;

    if (!replyMessage.trim()) {
      toast.error('Scrivi un messaggio di risposta');
      return;
    }

    setReplyLoading(true);
    await updateRequestStatus(selectedRequest.id, actionType, replyMessage);
    setReplyLoading(false);
  };

  const openReplyModal = (request: AdoptionRequest, action: 'approved' | 'rejected') => {
    setSelectedRequest(request);
    setActionType(action);
    setReplyModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'In attesa', icon: Clock },
      approved: { variant: 'default' as const, label: 'Approvata', icon: Check },
      rejected: { variant: 'destructive' as const, label: 'Rifiutata', icon: X }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = {
    pending: requests.filter(r => r.status === 'pending'),
    approved: requests.filter(r => r.status === 'approved'),
    rejected: requests.filter(r => r.status === 'rejected'),
    all: requests
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Richieste per {petName}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-4 border rounded-lg animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-muted rounded-full" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-muted rounded w-1/3" />
                        <div className="h-3 bg-muted rounded w-2/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Nessuna richiesta</h3>
                <p className="text-muted-foreground">
                  Non ci sono ancora richieste per questo annuncio
                </p>
              </div>
            ) : (
              <Tabs defaultValue="pending" className="space-y-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="pending" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    In Attesa ({filteredRequests.pending.length})
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Approvate ({filteredRequests.approved.length})
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Rifiutate ({filteredRequests.rejected.length})
                  </TabsTrigger>
                  <TabsTrigger value="all" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Tutte ({filteredRequests.all.length})
                  </TabsTrigger>
                </TabsList>

                {Object.entries(filteredRequests).map(([status, statusRequests]) => (
                  <TabsContent key={status} value={status} className="space-y-3 max-h-96 overflow-auto">
                    {statusRequests.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted-foreground">
                          Nessuna richiesta {status === 'all' ? '' : status === 'pending' ? 'in attesa' : status === 'approved' ? 'approvata' : 'rifiutata'}
                        </p>
                      </div>
                    ) : (
                      statusRequests.map((request) => (
                        <div key={request.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={request.requester_profile.avatar_url || undefined} />
                                <AvatarFallback>
                                  {request.requester_profile.username.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h4 className="font-medium text-white">
                                  {request.requester_profile.display_name || request.requester_profile.username}
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  @{request.requester_profile.username}
                                </p>
                              </div>
                            </div>
                            {getStatusBadge(request.status)}
                          </div>

                          <p className="text-sm text-muted-foreground bg-muted/10 p-3 rounded">
                            {request.message}
                          </p>

                          {request.meeting_availability && (
                            <div className="text-xs text-muted-foreground">
                              <strong>Disponibilità:</strong> {request.meeting_availability}
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {formatDate(request.created_at)}
                            </span>
                            
                            {request.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openReplyModal(request, 'approved')}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  Approva
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => openReplyModal(request, 'rejected')}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Rifiuta
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approved' ? 'Approva Richiesta' : 'Rifiuta Richiesta'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-muted/10 p-4 rounded-lg">
                <p className="text-sm font-medium text-white">
                  {selectedRequest.requester_profile.display_name || selectedRequest.requester_profile.username}
                </p>
                <p className="text-xs text-muted-foreground">
                  Richiesta per {petName}
                </p>
              </div>

              <div>
                <Label htmlFor="reply">Messaggio di risposta *</Label>
                <Textarea
                  id="reply"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder={actionType === 'approved' 
                    ? "Complimenti! La tua richiesta è stata approvata. Contattami per organizzare l'incontro..."
                    : "Grazie per l'interesse, ma al momento non posso procedere con la tua richiesta perché..."
                  }
                  rows={4}
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setReplyModalOpen(false)}
                  className="flex-1"
                  disabled={replyLoading}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleReply}
                  disabled={replyLoading || !replyMessage.trim()}
                  className={`flex-1 ${actionType === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  variant={actionType === 'approved' ? 'default' : 'destructive'}
                >
                  {actionType === 'approved' ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  {replyLoading ? 'Invio...' : (actionType === 'approved' ? 'Approva' : 'Rifiuta')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}